import Image from "next/image";
import { useRouter } from "next/router";
import React, { useContext, useEffect, useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { observer } from "mobx-react";
import { SnackbarContext } from "../../components/alerts/snackbar";
import { MobileContainer } from "../../components/layouts/app/mobile-container";
import { BottomSheet } from "../../components/modals/bottom-sheet/bottom-sheet";
import { SellerProfile } from "../../stores/seller-store/seller-store.model";
import { useStores } from "../../stores";
import {
  SHOW_DISCLAIMER_DATA_KEY,
  MSG_GLOBAL_ERROR,
} from "../../utils/constants";
import { isCrediMart, isCrediStore } from "../../utils/helper";
import { save } from "../../utils/storage";
import { normalizePhoneWithoutZero } from "../../utils/string";
import { ChooseAuthMethod } from "./components/choose-auth-method";
import { InputPhoneNumber } from "./components/input-phone-number";
import { OtpVerification } from "./components/otp-verification";
import { trackEvents } from "../../utils/analytics";
import { SellerPageRouter } from "../seller/page-router.props";
import { LoadingOverlay } from "../../components/loading";

enum AuthStep {
  InputPhoneNumber,
  ChooseAuthMethod,
  OtpVerification,
}

interface AuthModuleProps {
  sellerData?: SellerProfile;
}

const AuthModule: React.FunctionComponent<AuthModuleProps> = observer(
  ({ sellerData }) => {
    const { accountStore, sellerStore, cartStore } = useStores();
    const router: SellerPageRouter = useRouter();

    const { showSnackbar } = useContext(SnackbarContext);

    const [currentStep, setCurrentStep] = useState<AuthStep>(
      AuthStep.InputPhoneNumber
    );
    const [phone, setPhone] = useState<string>("");
    const [method, setMethod] = useState<"wa" | "sms">(null);
    const [sendType, setSendType] = useState<string>(null);
    const [showSuccessSheet, setShowSuccessSheet] = useState<boolean>(false);
    const [isSubmitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
      const { seller: sellerUsername } = router.query;
      if (sellerUsername && !sellerData) {
        sellerStore?.getSellerProfile({ username: String(sellerUsername) });
      } else {
        sellerStore?.setSellerProfile(sellerData);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query.seller]);

    useEffect(() => {
      const { phone: phoneQuery, method: methodQuery } = router.query;

      if (phoneQuery && !methodQuery) {
        setPhone(String(normalizePhoneWithoutZero(phoneQuery)));
        setCurrentStep(AuthStep.ChooseAuthMethod);
      }
      if (phoneQuery && methodQuery) {
        if (String(methodQuery) === "wa") setMethod("wa");
        if (String(methodQuery) === "sms") setMethod("sms");
        setCurrentStep(AuthStep.OtpVerification);
      }
      if (!phoneQuery && !methodQuery) {
        setCurrentStep(AuthStep.InputPhoneNumber);
      }
    }, [router.query]);

    const handlePhoneNumberInput = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const val = event.target.value;
      const raw = val?.split(".").join("");
      const changeVal = Number(raw);
      if ((changeVal < 0 || isNaN(changeVal)) && val !== "") {
        return;
      }
      setPhone(normalizePhoneWithoutZero(raw));
    };

    const handleSubmitPhoneNumber = () => {
      setCurrentStep(AuthStep.ChooseAuthMethod);
      const { pathname, query } = router;
      router.query.phone = normalizePhoneWithoutZero(phone);
      router.push({ pathname, query });
    };

    const handleSubmitMethod = async (method: "wa" | "sms", recaptchaToken: string) => {
      console.log(method)
      try {
        setMethod(method);
        const sendType = await accountStore.requestOtp(
          phone,
          method,
          recaptchaToken
        );
        setSendType(sendType);
        setCurrentStep(AuthStep.OtpVerification);
        const { pathname, query } = router;
        router.query.method = method;
        router.push({ pathname, query });
      } catch (error) {
        showSnackbar({
          text: error?.message || "Terjadi kesalahan mohon coba kembali",
          type: "danger",
        });
      }
    };

    const handleChangePhoneNumber = () => {
      setCurrentStep(AuthStep.InputPhoneNumber);
      const { pathname, query } = router;
      const { phone: phoneQuery, method: methodQuery } = query;
      if (phoneQuery) delete router.query.phone;
      if (methodQuery) delete router.query.method;
      router.push({ pathname, query });
      if (sellerData?.isReseller) {
        trackEvents({
          sellerUsername: sellerData?.User?.username,
          eventName: "cm_pesan_auth",
          eventCategory: "auth",
          eventLabel: "insert phone number",
          trackers: ["GTM", "AF"],
        });
      }
    };

    const handleResendOtp = async (recaptchaToken: string) => {
      try {
        const sendType = await accountStore.requestOtp(
          phone,
          method,
          recaptchaToken
        );
        setSendType(sendType);
        setCurrentStep(AuthStep.OtpVerification);
      } catch (error) {
        showSnackbar({
          text: error?.message || "Terjadi kesalahan mohon coba kembali",
          type: "danger",
        });
      }
    };

    const handleSubmitOtp = async (otp: string) => {
      try {
        setSubmitting(true);
        const isOtpVerified = await accountStore.verifyOtp(
          phone,
          sendType,
          otp
        );
        setSubmitting(false);
        if (isOtpVerified) {
          setShowSuccessSheet(true);
        } else {
          showSnackbar({
            text: "OTP tidak valid. Silakan coba lagi",
            type: "danger",
          });
        }
      } catch (error) {
        setSubmitting(false);
        showSnackbar({
          text: error?.message || "Terjadi kesalahan mohon coba kembali",
          type: "danger",
        });
      }
    };

    const goToProductsPage = () => {
      router.replace(`/${router.query.seller}`);
    };

    const handleAuthSuccess = async () => {
      const { seller: sellerUsername, from, switchAccount } = router.query;

      setShowSuccessSheet(false);

      // flow Ubah btn
      if (
        from &&
        from === "checkout" &&
        switchAccount &&
        switchAccount === "true"
      ) {
        // update checkout data with logged in user's phone, name & address
        const buyerAccount = await accountStore?.getBuyerAccount();
        await cartStore.saveCheckoutData({
          ...cartStore.savedCheckoutData,
          customerName: buyerAccount?.name,
          customerPhone: phone,
          customerAddress: {
            address: buyerAccount?.address,
            latitude: buyerAccount?.latitude,
            longitude: buyerAccount?.longitude,
            stateId: null,
            cityId: null,
            districtId: null,
            subdistrictId: null,
            zipcode: null,
            notes: buyerAccount?.additionalAddress, // address notes
            placeId: null,
          },
        });
      }

      if (isCrediStore()) {
        if (!!from && from === "checkout") {
          if (switchAccount && switchAccount === "true") {
            // flow Ubah btn
            save(SHOW_DISCLAIMER_DATA_KEY, true);
            router.replace(`/${sellerUsername}/checkout`);
          } else {
            try {
              await cartStore.createOrder();
              router.replace(`/${sellerUsername}/order/success`);
            } catch (error) {
              save(SHOW_DISCLAIMER_DATA_KEY, true);
              router.replace(`/${sellerUsername}/checkout`);
              showSnackbar({
                text: error?.message || MSG_GLOBAL_ERROR,
                type: "danger",
              });
            }
          }
        } else if (!!from && from === "order") {
          router.replace(`/${sellerUsername}/order`);
        } else {
          goToProductsPage();
        }
      }

      if (isCrediMart()) {
        if (from && (from === "cart" || from === "checkout")) {
          // show disclaimer on credistore & all credimart account except trading & reseller account
          if (!sellerData?.isTrading && !sellerData?.isReseller) {
            save(SHOW_DISCLAIMER_DATA_KEY, true);
          }
          router.replace(`/${sellerUsername}/checkout`);
        } else if (!!from && from === "order") {
          router.replace(`/${sellerUsername}/order`);
        } else {
          goToProductsPage();
        }
      }
    };

    const renderSuccessBottomSheet = () => {
      return (
        showSuccessSheet && (
          <BottomSheet
            visible={showSuccessSheet}
            onDismiss={() => setShowSuccessSheet(false)}
            shouldCloseOnOverlayClick={false}
          >
            <div className="flex flex-col items-center justify-center">
              <Image
                src={`/img/${process.env.NEXT_PUBLIC_APP_NAME}/verification.svg`}
                alt="verified"
                width={226}
                height={198}
              />
              <h1 className="text-primary text-xl mt-4 mb-2">
                Verifikasi Berhasil
              </h1>
              <p className="mb-8">
                Verifikasi Anda telah berhasil. Yuk mulai belanja sekarang!
              </p>
              <button
                onClick={handleAuthSuccess}
                className="bg-primary text-white p-3 flex justify-center rounded w-full max-w-md"
                id="auth-success-sheet-continue-btn"
                data-testid="auth-success-sheet-continue-btn"
              >
                LANJUTKAN
              </button>
            </div>
          </BottomSheet>
        )
      );
    };

    return (
      <MobileContainer
        backNavigation={
          currentStep > AuthStep.InputPhoneNumber ||
          router.query.from === "checkout"
        }
        bottomNavigation
        containerClassName="relative"
        headerClassName="bg-transparent"
        fixedHeader
        sellerData={sellerData}
      >
        {isSubmitting && <LoadingOverlay visible />}
        <GoogleReCaptchaProvider
          reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
        >
          {currentStep === AuthStep.InputPhoneNumber ? (
            <InputPhoneNumber
              phoneNumber={phone}
              onPhoneNumberChange={handlePhoneNumberInput}
              onSubmit={handleSubmitPhoneNumber}
            />
          ) : currentStep === AuthStep.ChooseAuthMethod ? (
            <ChooseAuthMethod
              phoneNumber={phone}
              onSubmit={handleSubmitMethod}
            />
          ) : (
            <OtpVerification
              phoneNumber={phone}
              onChangePhoneNumber={handleChangePhoneNumber}
              onResendOtp={handleResendOtp}
              onSubmit={handleSubmitOtp}
            />
          )}
        </GoogleReCaptchaProvider>

        {renderSuccessBottomSheet()}
      </MobileContainer>
    );
  }
);

export default AuthModule;
